import { LogLevel } from '@relayprotocol/relay-sdk'
import type { Abi, Address, Hex } from 'viem'

export { LogLevel }

// https://docs.relay.link/references/api/get-intents-status-v2
export interface RelayExecutionInfo {
  status: RelayExecutionStatus
  details: string
  /** Incoming transaction hashes */
  inTxHashes: string[]
  /** Outgoing transaction hashes */
  txHashes: string[]
  /** The last timestamp the data was updated in milliseconds */
  time: number
  originChainId: number
  destinationChainId: number
}

// https://docs.relay.link/references/api/get-token-price
export interface RelayTokenPriceInfo {
  price: number
}

//// The types below are duplicated from @funkit/api-base atm, but this package should be used in BE as well
//// TODO: Are we fine with BE importing @funkit/api-base and (by extension) @funkit/utils?
//// See https://linear.app/funxyz/issue/PE-1342/sdk-extract-domainsrelayts-to-a-new-package#comment-d487d533

export interface Logger {
  error(message: string, data?: object): void
  info(message: string, data?: object): void
}

// Reference: https://github.com/reservoirprotocol/relay-kit/blob/211c645f9702a3b459ff545aa4e2e9d536c38455/packages/sdk/src/types/Execute.ts#L54-L61
export enum RelayExecutionStatus {
  DELAYED = 'delayed',
  FAILURE = 'failure',
  PENDING = 'pending',
  REFUND = 'refund',
  SUCCESS = 'success',
  WAITING = 'waiting',
  UNKNOWN = 'unknown',
}

export interface BaseApiFunkitCheckoutActionParams {
  contractAddress: Address
  value?: bigint
}

interface ApiFunkitCheckoutActionParamsRawCalldata extends BaseApiFunkitCheckoutActionParams {
  rawCalldata: Hex
  contractAbi?: never
  functionName?: never
  functionArgs?: never
}
interface ApiFunkitCheckoutActionParamsContractAbi extends BaseApiFunkitCheckoutActionParams {
  rawCalldata?: never
  contractAbi: Abi
  functionName: string
  functionArgs: unknown[]
}

export type ApiFunkitCheckoutActionParams =
  | ApiFunkitCheckoutActionParamsRawCalldata
  | ApiFunkitCheckoutActionParamsContractAbi

export enum CheckoutRefundState {
  INITIATED = 'INITIATED',
  ERROR = 'ERROR',
  REFUNDED = 'REFUNDED',
  PROCEEDED = 'PROCEEDED',
  WAITING_FOR_FULFILLMENT = 'WAITING_FOR_FULFILLMENT',
  FULFILLED = 'FULFILLED',
}

// Reference from api server: https://github.com/fun-xyz/fun-api-server/blob/main/src/tables/FunWalletCheckout.ts#L11C1-L21C2
export enum CheckoutState {
  // In-progress States
  FROM_UNFUNDED = 'FROM_UNFUNDED',
  FROM_FUNDED = 'FROM_FUNDED',
  FROM_POOLED = 'FROM_POOLED',
  TO_UNFUNDED = 'TO_UNFUNDED',
  TO_FUNDED = 'TO_FUNDED',
  TO_POOLED = 'TO_POOLED',
  TO_READY = 'TO_READY',
  PENDING_RECEIVAL = 'PENDING_RECEIVAL',
  // Terminal States
  COMPLETED = 'COMPLETED',
  CHECKOUT_ERROR = 'CHECKOUT_ERROR',
  EXPIRED = 'EXPIRED',
  CANCELLED = 'CANCELLED',
}

// The response of the actual /checkout/quote api
export type CheckoutApiQuoteResponse = {
  quoteId: string
  estTotalFromAmountBaseUnit: string
  estSubtotalFromAmountBaseUnit: string
  estFeesFromAmountBaseUnit: string
  fromTokenAddress: Address
  estFeesUsd: number
  estSubtotalUsd: number
  estTotalUsd: number
  estCheckoutTimeMs: number
  estMarketMakerGasUsd: number
  lpFeePercentage: number
  lpFeeUsd: number
}

// The formatted response quote interface from the core sdk
export type CheckoutQuoteResponse = CheckoutApiQuoteResponse & {
  estTotalFromAmount: string
  estSubtotalFromAmount: string
  estFeesFromAmount: string

  // Any additional fields purely for frontend use
  metadata?: { [key: string]: unknown }
}
